/**
 * ExceptionView — "I ran into something; which way?"
 *
 * Story 26739. Visual contract: artboard (3) in
 * `tasks/design/step-views.html`.
 *
 * A RESTATEMENT STACK, then one sentence naming the conflict, then stacked
 * full-width pills. The restatement is the reason this is not just the brief
 * with different copy: an exception reaches somebody who has been away from
 * this run — often for hours, from an email — so the pane re-establishes what
 * the run is about (client and policy), what the record says now, what the
 * email asked for, and where the request came from, BEFORE it asks anything.
 * Without that they have to reconstruct it from a question that assumes they
 * already know.
 *
 * "Launched from" carries the hop back to the original request. The row itself
 * is a slot (`sourceRequestRow`) because opening a message is an Office.js
 * call and shared code may not make one — the add-in passes its
 * `SourceRequestRow`, ora passes a plain line or nothing at all.
 *
 * NEVER A FORM. The exception gates offer a choice, not a data-entry surface.
 * Anything that needs typing belongs to the brief.
 *
 * The option labels come from `getChoiceLabel` — the SAME helper the shared
 * `HitlChoicePanel` pills use — so a gate that relabels approve/reject
 * ("Retry" / "Stop workflow" on the record-locked gate) reads the same here as
 * it does in the drawer. `onChoose` is handed the RAW choice value, never the
 * label: the label is display only and the decision contract (schema enum,
 * x-destructive marker, resume validation) rides the raw string.
 *
 * DEVIATION FROM THE ARTBOARD, RECORDED. The design draws exactly two pills
 * and every exception gate in the substrate declares exactly two, so that is
 * what renders in practice — but this component renders whatever the gate
 * DECLARES rather than slicing to two. Hiding a third real option would answer
 * a question on the Servicer's behalf by omission, which is worse than a pane
 * that is one pill taller than the artboard.
 *
 * No Office.js, no Remix server import (design Constraint 20).
 */
import type { ReactNode } from "react";
import { StepViewFrame, identityLine } from "./StepViewFrame";
import {
  getChoiceLabel,
  getChoices,
} from "../components/HitlChoicePanel";
import { NEEDS_YOU_LABEL } from "../statusLabels";

export interface ExceptionViewProps {
  clientName?: string | null;
  policyNumber?: string | null;
  /** "What it says now" — the value already on the record. */
  currentValue?: ReactNode;
  /** "What the email asks for". */
  requestedValue?: ReactNode;
  /**
   * "Launched from" — the hop back to the original request. A NODE, because
   * opening a message is an Office.js call the add-in owns.
   */
  sourceRequestRow?: ReactNode | null;
  /** The one sentence naming the conflict, in ora's voice. */
  conflictSentence?: ReactNode;
  /** The live gate. Supplies the choices and their labels. */
  hitlQuestion?: Record<string, unknown> | null;
  /** Called with the RAW choice value. The host owns the latch + transport. */
  onChoose?: (choice: string) => void;
  /** True while an answer is in flight — disables every pill. */
  isSubmitting?: boolean;
  /** Rendered between the restatement stack and the conflict sentence. */
  children?: ReactNode;
}

function RestateRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-0.5 border-b border-primary-gray-light-200 py-[7px] last:border-b-0"
      data-testid="exception-restate-row"
    >
      <span className="text-[9.5px] uppercase tracking-[0.06em] text-primary-gray-light-500">
        {label}
      </span>
      <span className="text-[11.5px] leading-snug text-primary-gray-light-900">
        {children}
      </span>
    </div>
  );
}

export function ExceptionView({
  clientName,
  policyNumber,
  currentValue,
  requestedValue,
  sourceRequestRow,
  conflictSentence,
  hitlQuestion,
  onChoose,
  isSubmitting = false,
  children,
}: ExceptionViewProps) {
  const identity = identityLine(clientName, policyNumber);
  const choices = hitlQuestion ? getChoices(hitlQuestion) : [];

  return (
    <StepViewFrame
      view="exception"
      eyebrow={NEEDS_YOU_LABEL}
      tone="attention"
      headline={identity}
    >
      <div className="flex flex-col" data-testid="exception-restatement">
        {identity ? (
          <RestateRow label="Client / Policy">{identity}</RestateRow>
        ) : null}
        {currentValue ? (
          <RestateRow label="What it says now">{currentValue}</RestateRow>
        ) : null}
        {requestedValue ? (
          <RestateRow label="What the email asks for">{requestedValue}</RestateRow>
        ) : null}
        {sourceRequestRow ? (
          <RestateRow label="Launched from">{sourceRequestRow}</RestateRow>
        ) : null}
      </div>

      {children}

      {conflictSentence ? (
        <p
          className="mt-1 text-[12.5px] leading-relaxed text-primary-gray-light-900"
          data-testid="exception-conflict"
        >
          {conflictSentence}
        </p>
      ) : null}

      {choices.length > 0 ? (
        <div className="flex flex-col gap-2" data-testid="exception-options">
          {choices.map((choice, index) => (
            <button
              key={choice}
              type="button"
              disabled={isSubmitting}
              onClick={() => onChoose?.(choice)}
              data-testid={`exception-choice-${choice
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              className={`w-full rounded-full border px-2.5 py-2 text-center text-xs font-semibold transition-shadow disabled:cursor-not-allowed disabled:opacity-60 ${
                index === 0
                  ? "border-base-black bg-sunrise text-primary-gray-light-900"
                  : "border-base-black bg-base-white text-primary-gray-light-900"
              }`}
            >
              {getChoiceLabel(hitlQuestion, choice) ?? choice}
            </button>
          ))}
        </div>
      ) : null}
    </StepViewFrame>
  );
}

export default ExceptionView;
